"use client";

/**
 * Shared embed shell for Scheduler questions.
 *
 * Each calendar provider registers a `SchedulerEmbedProvider` that knows how
 * to mount its official widget and listen for booking completion. This
 * component owns everything provider-agnostic:
 *   - settings / embed-config fetching
 *   - loading overlay + error states
 *   - form field registration + booking confirmation card
 *   - Calendly-specific server resolution after postMessage (via provider id)
 */

import { trpc } from "@plura/shared/trpc";
import { FormJourneyItem, SchedulerSettings } from "@plura/shared/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import BookingConfirmationCard from "../BookingConfirmationCard";
import EmbedLoadingSkeleton from "../EmbedLoadingSkeleton";
import type { SchedulerBookingValue, SchedulerEmbedProvider } from "./types";
import { isCalendlyScheduledEvent } from "./providers/calendly";
import { parseStoredBooking, recallInviteeValue } from "./utils";

interface Props {
  step: FormJourneyItem;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  handleAnswer?: () => void;
  embeddedInline?: boolean;
  provider: SchedulerEmbedProvider;
}

const EmbedSchedulerSlot = ({
  step,
  editorMode,
  previewMode,
  active,
  handleAnswer,
  embeddedInline = false,
  provider,
}: Props) => {
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues();

  const settings = useMemo<SchedulerSettings | null>(() => {
    const parsed = SchedulerSettings.safeParse(step.schedulerSettings);
    return parsed.success ? parsed.data : null;
  }, [step.schedulerSettings]);

  const isRuntime = !editorMode || (editorMode && previewMode);
  const widgetEnabled = !!settings && (active || editorMode);

  const recalledName = useMemo(
    () => recallInviteeValue(userResponses, settings?.inviteeNameBlockId, "name"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings?.inviteeNameBlockId, widgetEnabled]
  );
  const recalledEmail = useMemo(
    () => recallInviteeValue(userResponses, settings?.inviteeEmailBlockId, "email"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings?.inviteeEmailBlockId, widgetEnabled]
  );

  const embedConfigQuery = trpc.scheduler.getEmbedConfigForBlock.useQuery(
    { blockId: step.blockId },
    { enabled: widgetEnabled, refetchOnWindowFocus: false, staleTime: 5 * 60_000 }
  );
  const recordMutation = trpc.scheduler.recordExternalBookingForBlock.useMutation();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const embedFrameRef = useRef<HTMLDivElement | null>(null);
  const [widgetMounted, setWidgetMounted] = useState(false);
  const [embedContentReady, setEmbedContentReady] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [googleConfirmEmail, setGoogleConfirmEmail] = useState("");
  const [googleConfirmName, setGoogleConfirmName] = useState("");
  const [googleConfirmError, setGoogleConfirmError] = useState<string | null>(null);
  const [booking, setBooking] = useState<SchedulerBookingValue | null>(() =>
    parseStoredBooking(userResponses[step.blockId])
  );

  useEffect(() => {
    if (recalledEmail && !googleConfirmEmail) setGoogleConfirmEmail(recalledEmail);
    if (recalledName && !googleConfirmName) setGoogleConfirmName(recalledName);
  }, [recalledEmail, recalledName, googleConfirmEmail, googleConfirmName]);

  useEffect(() => {
    form.register(step.blockId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.blockId]);

  const persistBooking = (value: SchedulerBookingValue) => {
    setBooking(value);
    form.setValue(step.blockId, JSON.stringify(value), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const advanceToNextStep = () => {
    if (!handleAnswer) return;
    window.setTimeout(() => handleAnswer(), 250);
  };

  useEffect(() => {
    if (!widgetEnabled || !embedConfigQuery.data || !containerRef.current || booking) return;

    let cancelled = false;
    setWidgetMounted(false);
    setEmbedContentReady(false);
    setOverlayVisible(true);
    setWidgetError(null);

    const cleanup = provider.mount({
      blockId: step.blockId,
      container: containerRef.current,
      embedConfig: embedConfigQuery.data,
      recalledName,
      recalledEmail,
      onReady: () => {
        if (!cancelled) setEmbedContentReady(true);
      },
      onError: (message) => {
        if (!cancelled) setWidgetError(message);
      },
      onBooking: (value) => {
        if (!cancelled) {
          persistBooking(value);
          advanceToNextStep();
        }
      },
    });

    setWidgetMounted(true);

    return () => {
      cancelled = true;
      cleanup();
      setWidgetMounted(false);
      setEmbedContentReady(false);
      setOverlayVisible(true);
    };
  }, [
    widgetEnabled,
    embedConfigQuery.data,
    recalledName,
    recalledEmail,
    booking,
    provider,
    step.blockId,
  ]);

  useEffect(() => {
    if (!embedContentReady) {
      setOverlayVisible(true);
      return;
    }
    const hideTimer = window.setTimeout(() => setOverlayVisible(false), 200);
    return () => window.clearTimeout(hideTimer);
  }, [embedContentReady]);

  useEffect(() => {
    if (!provider.useReadyListener) return;
    return provider.useReadyListener({
      widgetMounted,
      booking: !!booking,
      onReady: () => setEmbedContentReady(true),
    });
  }, [provider, widgetMounted, booking]);

  useEffect(() => {
    if (
      embeddedInline ||
      !provider.useSizeSync ||
      !provider.syncEmbedToPanelHeight ||
      !embedContentReady ||
      booking
    )
      return;
    const frame = embedFrameRef.current;
    if (!frame) return;
    return provider.useSizeSync({
      embedFrame: frame,
      container: containerRef.current,
      embedContentReady,
      booking: !!booking,
    });
  }, [provider, embeddedInline, embedContentReady, booking]);

  // Calendly resolves booking details server-side from postMessage URIs.
  useEffect(() => {
    if (provider.id !== "CALENDLY" || !isRuntime || !widgetEnabled || booking) return;

    const handler = (event: MessageEvent) => {
      if (!event.origin?.endsWith("calendly.com")) return;
      if (!isCalendlyScheduledEvent(event.data)) return;
      const { event: scheduledEvent, invitee } = event.data.payload;

      recordMutation
        .mutateAsync({
          blockId: step.blockId,
          eventUri: scheduledEvent.uri,
          inviteeUri: invitee.uri,
        })
        .then((result) => {
          persistBooking({
            startTime: result.startTime,
            endTime: result.endTime,
            inviteeName: result.inviteeName,
            inviteeEmail: result.inviteeEmail,
            externalId: result.externalId,
            externalUrl: result.externalUrl,
            meetUrl: result.meetUrl,
          });
          advanceToNextStep();
        })
        .catch((e: Error) => {
          persistBooking({
            startTime: new Date().toISOString(),
            endTime: new Date().toISOString(),
            inviteeName: recalledName || "",
            inviteeEmail: recalledEmail || "",
            externalId: scheduledEvent.uri,
            externalUrl: scheduledEvent.uri,
            meetUrl: null,
          });
          setWidgetError(
            `Booking confirmed on Calendly but we couldn't sync the details: ${e.message}. Check your inbox for the confirmation.`
          );
          advanceToNextStep();
        });
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id, isRuntime, widgetEnabled, booking, step.blockId, recordMutation, recalledName, recalledEmail]);

  const handleGoogleContinue = async () => {
    if (!isRuntime) return;
    const email = (googleConfirmEmail || recalledEmail).trim();
    const name = (googleConfirmName || recalledName).trim();
    if (!email) {
      setGoogleConfirmError("Enter the email you used to book so we can confirm your appointment.");
      return;
    }
    setGoogleConfirmError(null);
    try {
      const result = await recordMutation.mutateAsync({
        blockId: step.blockId,
        inviteeEmail: email,
        inviteeName: name || undefined,
      });
      persistBooking({
        startTime: result.startTime,
        endTime: result.endTime,
        inviteeName: result.inviteeName,
        inviteeEmail: result.inviteeEmail,
        externalId: result.externalId,
        externalUrl: result.externalUrl,
        meetUrl: result.meetUrl,
      });
      advanceToNextStep();
    } catch (e) {
      setGoogleConfirmError(
        e instanceof Error ? e.message : "Couldn't confirm your booking yet. Try again in a moment."
      );
    }
  };

  const settingsMissing = !settings || !settings.connectionId || !settings.calendarId;

  if (settingsMissing) {
    return (
      <div className="unconfiguredNotice">
        Connect a calendar in <strong>Setup</strong> to enable booking for this question.
      </div>
    );
  }

  if (booking) {
    return (
      <BookingConfirmationCard
        startTime={booking.startTime}
        endTime={booking.endTime}
        inviteeName={booking.inviteeName}
        inviteeEmail={booking.inviteeEmail}
        meetUrl={booking.meetUrl}
        externalUrl={booking.externalUrl}
        isPending={booking.externalId.startsWith("pending:")}
      />
    );
  }

  if (embedConfigQuery.isError) {
    return (
      <div className="embedError">
        {embedConfigQuery.error.message || `Couldn't load the ${provider.label} booking widget.`}
      </div>
    );
  }

  if (embedConfigQuery.isSuccess && !embedConfigQuery.data) {
    return (
      <div className="embedError">
        Couldn&apos;t load the {provider.label} booking widget for this event type.
      </div>
    );
  }

  return (
    <div className="schedulerEmbedStack">
      <div
        ref={embedFrameRef}
        className={`embedFrame${embedContentReady ? " embedFrame--ready" : ""}${provider.id === "GOOGLE_CALENDAR" ? " embedFrame--iframe" : ""}`}
      >
        <div ref={containerRef} className="embedSlot" />
        {(overlayVisible || embedConfigQuery.isLoading) && !widgetError && <EmbedLoadingSkeleton />}
        {widgetError && <div className="embedError embedErrorOverlay">{widgetError}</div>}
      </div>

      {provider.id === "GOOGLE_CALENDAR" && embedContentReady && isRuntime && (
        <div className="googleEmbedContinue">
          <p className="googleEmbedContinue__hint">
            After you confirm your appointment above, enter the email you booked with and continue.
          </p>
          {!recalledEmail && (
            <label className="googleEmbedContinue__field">
              <span>Email used to book</span>
              <input
                type="email"
                value={googleConfirmEmail}
                onChange={(e) => setGoogleConfirmEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </label>
          )}
          <button
            type="button"
            className="googleEmbedContinue__cta"
            onClick={handleGoogleContinue}
            disabled={recordMutation.isPending}
          >
            {recordMutation.isPending ? "Confirming booking…" : "Continue"}
          </button>
          {googleConfirmError && <p className="googleEmbedContinue__error">{googleConfirmError}</p>}
        </div>
      )}
    </div>
  );
};

export default EmbedSchedulerSlot;
